import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  getOrganization, updateOrganization, getOrganizationStats,
  getEmployees, inviteEmployee, removeEmployee, getEmployeeClients,
  getClients, verifyClient, reassignClient, inviteClient,
  getQRCode, regenerateJoin
} from '../services/businessService';
import {
  assignToClient, unassignFromClient, getClientSchedule,
  getClientProgress, getClientActivitySummary,
  getAvailablePrograms, getAvailableWorkouts, getClientNutritionPlans
} from '../services/businessFitnessService';
import { FaPlus, FaCog, FaDumbbell, FaUtensils, FaTrash, FaChevronDown, FaChevronUp, FaRunning, FaClipboardList } from 'react-icons/fa';
import { NutritionPlannerWizard } from './NutritionPlanner';
import './BusinessDashboard.css';

export default function BusinessDashboard() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Organization
  const [org, setOrg] = useState(null);
  const [stats, setStats] = useState(null);
  const [editingOrg, setEditingOrg] = useState(false);
  const [orgForm, setOrgForm] = useState({
    name: '', businessType: '', description: '',
    branding: { logoUrl: '', primaryColor: '#16a34a', tagline: '' },
    ownerProfile: { name: '', title: '', phone: '', email: '' },
    practice: { phone: '', website: '', address: '', city: '', state: '', zip: '', specializations: [] }
  });

  // Employees
  const [employees, setEmployees] = useState([]);
  const [employeeClientCounts, setEmployeeClientCounts] = useState({});
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', title: '' });
  const [inviting, setInviting] = useState(false);

  // Clients
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [clientStatusFilter, setClientStatusFilter] = useState('');
  const [clientInviteForm, setClientInviteForm] = useState({ email: '', name: '' });
  const [invitingClient, setInvitingClient] = useState(false);
  const [showClientInviteForm, setShowClientInviteForm] = useState(false);

  // Client fitness/activity management
  const [expandedClient, setExpandedClient] = useState(null);
  const [assigningClient, setAssigningClient] = useState(null);
  const [managingClient, setManagingClient] = useState(null);
  const [clientSchedules, setClientSchedules] = useState({});
  const [clientProgressData, setClientProgressData] = useState({});
  const [clientActivitySummaries, setClientActivitySummaries] = useState({});
  const [clientNutritionData, setClientNutritionData] = useState({});
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const [availableWorkouts, setAvailableWorkouts] = useState([]);
  const [assignType, setAssignType] = useState('program');
  const [assignContentId, setAssignContentId] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [nutritionPlannerMember, setNutritionPlannerMember] = useState(null);

  // Join Settings
  const [qrData, setQrData] = useState(null);
  const [requireVerification, setRequireVerification] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // Billing
  const [tier, setTier] = useState(null);
  const [limits, setLimits] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const [orgRes, statsRes, empRes, clientRes] = await Promise.all([
        getOrganization(),
        getOrganizationStats(),
        getEmployees(),
        getClients()
      ]);
      setOrg(orgRes);
      const o = orgRes.organization || orgRes;
      setOrgForm({
        name: o.name || '',
        businessType: o.businessType || '',
        description: o.description || '',
        branding: {
          logoUrl: o.branding?.logoUrl || '',
          primaryColor: o.branding?.primaryColor || '#16a34a',
          tagline: o.branding?.tagline || ''
        },
        ownerProfile: {
          name: o.ownerProfile?.name || '',
          title: o.ownerProfile?.title || '',
          phone: o.ownerProfile?.phone || '',
          email: o.ownerProfile?.email || ''
        },
        practice: {
          phone: o.practice?.phone || '',
          website: o.practice?.website || '',
          address: o.practice?.address || '',
          city: o.practice?.city || '',
          state: o.practice?.state || '',
          zip: o.practice?.zip || '',
          specializations: o.practice?.specializations || []
        }
      });
      setRequireVerification(orgRes.requireVerification ?? false);
      setStats(statsRes);
      setEmployees(empRes.employees || empRes || []);
      setClients(clientRes.clients || clientRes || []);
      setTier(orgRes.subscriptionTier || orgRes.tier || 'free');
      setLimits(statsRes.limits || null);

      const empList = empRes.employees || empRes || [];
      const counts = {};
      await Promise.all(empList.map(async (emp) => {
        try {
          const res = await getEmployeeClients(emp._id);
          counts[emp._id] = (res.clients || res || []).length;
        } catch { counts[emp._id] = 0; }
      }));
      setEmployeeClientCounts(counts);
    } catch (err) {
      toast.error(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadQRData = async () => {
    try {
      const data = await getQRCode();
      setQrData(data);
    } catch (err) {
      toast.error(err.message || 'Failed to load QR code');
    }
  };

  useEffect(() => {
    if (activeTab === 'join') loadQRData();
  }, [activeTab]);

  // --- Org ---
  const handleSaveOrg = async () => {
    try {
      const updated = await updateOrganization({
        name: orgForm.name,
        businessType: orgForm.businessType,
        description: orgForm.description,
        branding: orgForm.branding,
        ownerProfile: orgForm.ownerProfile,
        practice: orgForm.practice
      });
      setOrg(updated.organization || updated);
      setEditingOrg(false);
      toast.success('Organization updated');
    } catch (err) {
      toast.error(err.message);
    }
  };

  // --- Employees ---
  const handleInviteEmployee = async (e) => {
    e.preventDefault();
    if (!inviteForm.email) return toast.error('Email is required');
    setInviting(true);
    try {
      const res = await inviteEmployee(inviteForm);
      setInviteForm({ email: '', name: '', title: '' });
      if (res.emailSent) {
        toast.success('Invite email sent successfully');
      } else {
        toast.success('Employee invited (email not configured — link copied)');
      }
      if (res.inviteUrl) {
        await navigator.clipboard.writeText(res.inviteUrl);
        toast.success('Invite link copied to clipboard');
      }
      const empRes = await getEmployees();
      setEmployees(empRes.employees || empRes || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveEmployee = async (empId, empName) => {
    if (!window.confirm(`Remove ${empName || 'this employee'}? Their clients will be unassigned.`)) return;
    try {
      await removeEmployee(empId);
      toast.success('Employee removed');
      setEmployees((prev) => prev.filter((e) => e._id !== empId));
    } catch (err) {
      toast.error(err.message);
    }
  };

  // --- Clients ---
  const filteredClients = useMemo(() => {
    let list = clients;
    if (clientSearch) {
      const q = clientSearch.toLowerCase();
      list = list.filter((c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q)
      );
    }
    if (clientStatusFilter) {
      list = list.filter((c) => c.status === clientStatusFilter);
    }
    return list;
  }, [clients, clientSearch, clientStatusFilter]);

  const handleVerifyClient = async (clientId) => {
    try {
      await verifyClient(clientId);
      toast.success('Client verified');
      setClients((prev) => prev.map((c) => c._id === clientId ? { ...c, status: 'active' } : c));
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleReassignClient = async (clientId, employeeId) => {
    try {
      await reassignClient(clientId, employeeId);
      toast.success('Client reassigned');
      const clientRes = await getClients();
      setClients(clientRes.clients || clientRes || []);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleInviteClient = async (e) => {
    e.preventDefault();
    if (!clientInviteForm.email) return toast.error('Email is required');
    setInvitingClient(true);
    try {
      const res = await inviteClient(clientInviteForm);
      if (res.emailSent) {
        toast.success('Invite email sent to client');
      } else {
        toast.success('Client invited (email not configured)');
      }
      setClientInviteForm({ email: '', name: '' });
      setShowClientInviteForm(false);
      const clientRes = await getClients();
      setClients(clientRes.clients || clientRes || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setInvitingClient(false);
    }
  };

  // --- Client Fitness Handlers ---
  const loadActivitySummary = useCallback(async (memberId) => {
    try {
      const summary = await getClientActivitySummary(memberId);
      setClientActivitySummaries(prev => ({ ...prev, [memberId]: summary }));
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    const activeClients = clients.filter(c => c.status === 'active' && c.userId);
    activeClients.forEach(c => loadActivitySummary(c._id));
  }, [clients, loadActivitySummary]);

  const loadContentLibrary = useCallback(async () => {
    try {
      const [progRes, workRes] = await Promise.all([
        getAvailablePrograms(),
        getAvailableWorkouts()
      ]);
      setAvailablePrograms(progRes.programs || []);
      setAvailableWorkouts(workRes.workouts || []);
    } catch { /* silent */ }
  }, []);

  const handleToggleExpand = (clientId) => {
    setExpandedClient(prev => prev === clientId ? null : clientId);
    setAssigningClient(null);
    setManagingClient(null);
  };

  const handleOpenAssign = async (clientId) => {
    if (assigningClient === clientId) {
      setAssigningClient(null);
      return;
    }
    setAssigningClient(clientId);
    setManagingClient(null);
    setAssignType('program');
    setAssignContentId('');
    setAssignNotes('');
    if (availablePrograms.length === 0) await loadContentLibrary();
  };

  const handleAssignContent = async (memberId) => {
    if (!assignContentId) {
      toast.error('Select something to assign');
      return;
    }
    try {
      const data = {
        type: assignType,
        ...(assignType === 'program' ? { programId: assignContentId } : { workoutId: assignContentId }),
        notes: assignNotes || ''
      };
      await assignToClient(memberId, data);
      toast.success('Content assigned to client');
      setAssigningClient(null);
      setAssignContentId('');
      setAssignNotes('');
      handleOpenManage(memberId);
      loadActivitySummary(memberId);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleOpenManage = async (memberId) => {
    if (managingClient === memberId) {
      setManagingClient(null);
      return;
    }
    setManagingClient(memberId);
    setAssigningClient(null);
    try {
      const [schedule, progress, nutritionRes] = await Promise.all([
        getClientSchedule(memberId),
        getClientProgress(memberId),
        getClientNutritionPlans(memberId)
      ]);
      setClientSchedules(prev => ({ ...prev, [memberId]: schedule }));
      setClientProgressData(prev => ({ ...prev, [memberId]: progress }));
      setClientNutritionData(prev => ({ ...prev, [memberId]: nutritionRes.plans || [] }));
    } catch (err) {
      toast.error(err.message || 'Failed to load client details');
    }
  };

  const handleUnassign = async (memberId, assignmentId) => {
    try {
      await unassignFromClient(memberId, assignmentId);
      toast.success('Assignment removed');
      const schedule = await getClientSchedule(memberId);
      setClientSchedules(prev => ({ ...prev, [memberId]: schedule }));
      loadActivitySummary(memberId);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDuration = (mins) => {
    if (!mins) return '—';
    if (mins < 60) return `${Math.round(mins)}m`;
    return `${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m`;
  };

  // --- Join Settings ---
  const handleToggleVerification = async () => {
    const newVal = !requireVerification;
    try {
      await updateOrganization({ requireVerification: newVal });
      setRequireVerification(newVal);
      toast.success(newVal ? 'Verification required for new clients' : 'Clients can join without verification');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Regenerate join link? The old link will stop working.')) return;
    setRegenerating(true);
    try {
      await regenerateJoin();
      await loadQRData();
      toast.success('Join link regenerated');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRegenerating(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Failed to copy');
    }
  };

  // --- Computed stats for overview ---
  const clientCounts = useMemo(() => ({
    active: clients.filter((c) => c.status === 'active').length,
    pending: clients.filter((c) => c.status === 'pending').length,
    invited: clients.filter((c) => c.status === 'invited').length,
    total: clients.length
  }), [clients]);

  // --- Tab definitions ---
  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'employees', label: 'Employees' },
    { key: 'clients', label: 'Clients' },
    { key: 'join', label: 'Join Settings' },
    { key: 'billing', label: 'Billing' }
  ];

  if (loading) {
    return (
      <div className="business-dashboard">
        <div className="business-loading">
          <div className="loading-spinner" />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="business-dashboard">
      <div className="business-dashboard-container">
        <div className="business-dashboard-header">
          <h1>{org?.name || 'Business Dashboard'}</h1>
          <p>Manage your organization, employees, and clients</p>
        </div>

        <div className="business-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`business-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="tab-content">
          {activeTab === 'overview' && renderOverview()}
          {activeTab === 'employees' && renderEmployees()}
          {activeTab === 'clients' && renderClients()}
          {activeTab === 'join' && renderJoinSettings()}
          {activeTab === 'billing' && renderBilling()}
        </div>
      </div>
    </div>
  );

  // ─── Overview Tab ──────────────────────────────────────────────
  function renderOverview() {
    const o = org?.organization || org;
    return (
      <div className="business-overview">
        <div className="business-org-info">
          {editingOrg ? (
            <div className="business-edit-org">
              <h3 className="business-edit-section-title">Organization</h3>
              <div className="business-form-row">
                <label>Organization Name</label>
                <input type="text" value={orgForm.name} onChange={(e) => setOrgForm({ ...orgForm, name: e.target.value })} placeholder="Organization name" />
              </div>
              <div className="business-form-row">
                <label>Business Type</label>
                <select value={orgForm.businessType} onChange={(e) => setOrgForm({ ...orgForm, businessType: e.target.value })}>
                  <option value="">Select type</option>
                  <option value="physical_therapy">Physical Therapy</option>
                  <option value="nutrition">Nutrition / Dietetics</option>
                  <option value="wellness">Wellness Center</option>
                  <option value="fitness">Fitness / Gym</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="business-form-row">
                <label>Description</label>
                <textarea value={orgForm.description} onChange={(e) => setOrgForm({ ...orgForm, description: e.target.value })} placeholder="Brief description" rows={2} maxLength={500} />
              </div>

              <h3 className="business-edit-section-title">Practice Details</h3>
              <div className="business-form-grid">
                <div className="business-form-row">
                  <label>Phone</label>
                  <input type="tel" value={orgForm.practice.phone} onChange={(e) => setOrgForm({ ...orgForm, practice: { ...orgForm.practice, phone: e.target.value } })} />
                </div>
                <div className="business-form-row">
                  <label>Website</label>
                  <input type="url" value={orgForm.practice.website} onChange={(e) => setOrgForm({ ...orgForm, practice: { ...orgForm.practice, website: e.target.value } })} />
                </div>
              </div>
              <div className="business-form-row">
                <label>Address</label>
                <input type="text" value={orgForm.practice.address} onChange={(e) => setOrgForm({ ...orgForm, practice: { ...orgForm.practice, address: e.target.value } })} />
              </div>
              <div className="business-form-grid business-form-grid-3">
                <div className="business-form-row">
                  <label>City</label>
                  <input type="text" value={orgForm.practice.city} onChange={(e) => setOrgForm({ ...orgForm, practice: { ...orgForm.practice, city: e.target.value } })} />
                </div>
                <div className="business-form-row">
                  <label>State</label>
                  <input type="text" value={orgForm.practice.state} onChange={(e) => setOrgForm({ ...orgForm, practice: { ...orgForm.practice, state: e.target.value } })} maxLength={2} />
                </div>
                <div className="business-form-row">
                  <label>ZIP</label>
                  <input type="text" value={orgForm.practice.zip} onChange={(e) => setOrgForm({ ...orgForm, practice: { ...orgForm.practice, zip: e.target.value } })} maxLength={10} />
                </div>
              </div>

              <h3 className="business-edit-section-title">Owner Profile</h3>
              <div className="business-form-grid">
                <div className="business-form-row">
                  <label>Name</label>
                  <input type="text" value={orgForm.ownerProfile.name} onChange={(e) => setOrgForm({ ...orgForm, ownerProfile: { ...orgForm.ownerProfile, name: e.target.value } })} />
                </div>
                <div className="business-form-row">
                  <label>Title</label>
                  <input type="text" value={orgForm.ownerProfile.title} onChange={(e) => setOrgForm({ ...orgForm, ownerProfile: { ...orgForm.ownerProfile, title: e.target.value } })} placeholder="e.g. Practice Director" />
                </div>
              </div>
              <div className="business-form-grid">
                <div className="business-form-row">
                  <label>Email</label>
                  <input type="email" value={orgForm.ownerProfile.email} onChange={(e) => setOrgForm({ ...orgForm, ownerProfile: { ...orgForm.ownerProfile, email: e.target.value } })} />
                </div>
                <div className="business-form-row">
                  <label>Phone</label>
                  <input type="tel" value={orgForm.ownerProfile.phone} onChange={(e) => setOrgForm({ ...orgForm, ownerProfile: { ...orgForm.ownerProfile, phone: e.target.value } })} />
                </div>
              </div>

              <h3 className="business-edit-section-title">Branding</h3>
              <div className="business-form-row">
                <label>Logo URL</label>
                <input type="url" value={orgForm.branding.logoUrl} onChange={(e) => setOrgForm({ ...orgForm, branding: { ...orgForm.branding, logoUrl: e.target.value } })} placeholder="https://..." />
              </div>
              <div className="business-form-grid">
                <div className="business-form-row">
                  <label>Brand Color</label>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <input type="color" value={orgForm.branding.primaryColor} onChange={(e) => setOrgForm({ ...orgForm, branding: { ...orgForm.branding, primaryColor: e.target.value } })} style={{ width: 40, height: 40, padding: 2, borderRadius: 8, border: '1px solid var(--shell-border)' }} />
                    <input type="text" value={orgForm.branding.primaryColor} onChange={(e) => setOrgForm({ ...orgForm, branding: { ...orgForm.branding, primaryColor: e.target.value } })} maxLength={7} style={{ width: 100 }} />
                  </div>
                </div>
                <div className="business-form-row">
                  <label>Tagline</label>
                  <input type="text" value={orgForm.branding.tagline} onChange={(e) => setOrgForm({ ...orgForm, branding: { ...orgForm.branding, tagline: e.target.value } })} maxLength={200} />
                </div>
              </div>

              <div className="business-edit-actions">
                <button className="business-btn business-btn-primary" onClick={handleSaveOrg}>Save Changes</button>
                <button className="business-btn business-btn-secondary" onClick={() => setEditingOrg(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div className="business-org-display">
              <div className="business-org-details">
                <div className="business-org-header-row">
                  {o?.branding?.logoUrl && (
                    <img src={o.branding.logoUrl} alt="" className="business-org-logo" />
                  )}
                  <div>
                    <h2>{o?.name}</h2>
                    {o?.branding?.tagline && <p className="business-org-tagline">{o.branding.tagline}</p>}
                  </div>
                </div>
                {o?.businessType && (
                  <span className="business-type-badge">{o.businessType.replace(/_/g, ' ')}</span>
                )}
                {o?.description && <p className="business-org-desc">{o.description}</p>}

                <div className="business-profile-grid">
                  {o?.ownerProfile?.name && (
                    <div className="business-profile-item">
                      <span className="business-profile-label">Owner</span>
                      <span>{o.ownerProfile.name}{o.ownerProfile.title ? ` — ${o.ownerProfile.title}` : ''}</span>
                    </div>
                  )}
                  {(o?.practice?.phone || o?.ownerProfile?.phone) && (
                    <div className="business-profile-item">
                      <span className="business-profile-label">Phone</span>
                      <span>{o.practice?.phone || o.ownerProfile.phone}</span>
                    </div>
                  )}
                  {o?.practice?.website && (
                    <div className="business-profile-item">
                      <span className="business-profile-label">Website</span>
                      <a href={o.practice.website} target="_blank" rel="noopener noreferrer">{o.practice.website}</a>
                    </div>
                  )}
                  {o?.practice?.address && (
                    <div className="business-profile-item">
                      <span className="business-profile-label">Address</span>
                      <span>{[o.practice.address, o.practice.city, o.practice.state, o.practice.zip].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                  {o?.practice?.specializations?.length > 0 && (
                    <div className="business-profile-item business-profile-item-full">
                      <span className="business-profile-label">Specializations</span>
                      <div className="business-spec-tags">
                        {o.practice.specializations.map(s => <span key={s} className="business-spec-tag">{s}</span>)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <button className="business-btn business-btn-secondary" onClick={() => setEditingOrg(true)}>
                Edit Profile
              </button>
            </div>
          )}
        </div>

        <div className="business-stats-grid">
          <div className="business-stat-card">
            <span className="business-stat-value">{employees.length}</span>
            <span className="business-stat-label">Employees</span>
          </div>
          <div className="business-stat-card">
            <span className="business-stat-value">{clientCounts.active}</span>
            <span className="business-stat-label">Active Clients</span>
          </div>
          <div className="business-stat-card">
            <span className="business-stat-value">{clientCounts.pending}</span>
            <span className="business-stat-label">Pending Clients</span>
          </div>
          <div className="business-stat-card">
            <span className="business-stat-value">{clientCounts.invited}</span>
            <span className="business-stat-label">Invited</span>
          </div>
        </div>

        <div className="business-tier-section">
          <span className={`business-tier-badge tier-${tier}`}>{tier || 'free'} plan</span>
          {stats?.usage && (
            <span className="business-usage-hint">
              {stats.usage.employees || employees.length} / {stats.limits?.maxEmployees ?? '∞'} employees
              {' · '}
              {stats.usage.clients || clients.length} / {stats.limits?.maxClients ?? '∞'} clients
            </span>
          )}
        </div>
      </div>
    );
  }

  // ─── Employees Tab ─────────────────────────────────────────────
  function renderEmployees() {
    return (
      <div className="business-employees">
        <div className="business-section-header">
          <h2>Employees</h2>
        </div>

        <form className="business-invite-form" onSubmit={handleInviteEmployee}>
          <h3>Invite Employee</h3>
          <div className="business-invite-fields">
            <input
              type="email"
              placeholder="Email *"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
              required
            />
            <input
              type="text"
              placeholder="Name"
              value={inviteForm.name}
              onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
            />
            <input
              type="text"
              placeholder="Title (e.g. Trainer)"
              value={inviteForm.title}
              onChange={(e) => setInviteForm({ ...inviteForm, title: e.target.value })}
            />
            <button type="submit" className="business-btn business-btn-primary" disabled={inviting}>
              {inviting ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </form>

        <div className="business-employee-list">
          {employees.length === 0 ? (
            <div className="business-empty-state">
              <p>No employees yet. Send an invite to get started.</p>
            </div>
          ) : (
            employees.map((emp) => (
              <div key={emp._id} className="business-employee-card">
                <div className="business-employee-info">
                  <strong>{emp.name || emp.email}</strong>
                  {emp.title && <span className="business-employee-title">{emp.title}</span>}
                  <span className="business-employee-email">{emp.email}</span>
                  <span className="business-employee-clients">
                    {employeeClientCounts[emp._id] ?? '—'} clients
                  </span>
                </div>
                <div className="business-employee-actions">
                  {emp.inviteLink && (
                    <button
                      className="business-btn business-btn-small"
                      onClick={() => copyToClipboard(emp.inviteLink, 'Invite link')}
                    >
                      Copy Link
                    </button>
                  )}
                  <button
                    className="business-btn business-btn-danger business-btn-small"
                    onClick={() => handleRemoveEmployee(emp._id, emp.name)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // ─── Clients Tab ───────────────────────────────────────────────
  function renderClients() {
    return (
      <div className="business-clients">
        <div className="business-section-header">
          <h2>Clients</h2>
          <button
            className="business-btn business-btn-primary"
            onClick={() => setShowClientInviteForm(!showClientInviteForm)}
          >
            {showClientInviteForm ? 'Cancel' : 'Invite Client'}
          </button>
        </div>

        {showClientInviteForm && (
          <form className="business-invite-form" onSubmit={handleInviteClient}>
            <h3>Invite Client</h3>
            <div className="business-invite-fields">
              <input type="email" placeholder="Email *" value={clientInviteForm.email}
                onChange={(e) => setClientInviteForm({ ...clientInviteForm, email: e.target.value })} required />
              <input type="text" placeholder="Name" value={clientInviteForm.name}
                onChange={(e) => setClientInviteForm({ ...clientInviteForm, name: e.target.value })} />
              <button type="submit" className="business-btn business-btn-primary" disabled={invitingClient}>
                {invitingClient ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </form>
        )}

        <div className="business-clients-toolbar">
          <input type="text" className="business-search-input" placeholder="Search clients..."
            value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} />
          <select className="business-filter-select" value={clientStatusFilter}
            onChange={(e) => setClientStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="invited">Invited</option>
          </select>
        </div>

        <div className="business-client-list">
          {filteredClients.length === 0 ? (
            <div className="business-empty-state">
              <p>{clients.length === 0 ? 'No clients yet.' : 'No clients match your filters.'}</p>
            </div>
          ) : (
            filteredClients.map((client) => {
              const summary = clientActivitySummaries[client._id];
              const isExpanded = expandedClient === client._id;
              const isActive = client.status === 'active';

              return (
                <div key={client._id} className={`business-client-card ${isExpanded ? 'expanded' : ''}`}>
                  <div className="business-client-row" onClick={() => isActive && handleToggleExpand(client._id)}>
                    <div className="business-client-info">
                      <strong>{client.name || client.email}</strong>
                      <span className="business-client-email">{client.email}</span>
                      {client.assignedEmployee && (
                        <span className="business-client-assigned">
                          Assigned to: {client.assignedEmployee.name || client.assignedEmployee.email || 'Employee'}
                        </span>
                      )}
                      {isActive && summary && (
                        <div className="business-client-activity-summary">
                          <span className="activity-chip"><FaDumbbell /> {summary.totalSessions} sessions</span>
                          {summary.sessionsThisWeek > 0 && (
                            <span className="activity-chip active"><FaRunning /> {summary.sessionsThisWeek} this week</span>
                          )}
                          {summary.assignedContentCount > 0 && (
                            <span className="activity-chip"><FaClipboardList /> {summary.assignedContentCount} assigned</span>
                          )}
                          {summary.activeNutritionPlan && (
                            <span className="activity-chip nutrition"><FaUtensils /> {summary.activeNutritionPlan.name}</span>
                          )}
                          {summary.lastWorkoutDate && (
                            <span className="activity-chip muted">Last: {formatDate(summary.lastWorkoutDate)}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="business-client-actions">
                      <span className={`business-status-badge status-${client.status}`}>{client.status}</span>
                      {client.status === 'pending' && (
                        <button className="business-btn business-btn-primary business-btn-small"
                          onClick={(e) => { e.stopPropagation(); handleVerifyClient(client._id); }}>Verify</button>
                      )}
                      <select className="business-reassign-select"
                        value={client.assignedEmployee?._id || ''}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleReassignClient(client._id, e.target.value)}>
                        <option value="">Unassigned</option>
                        {employees.map((emp) => (
                          <option key={emp._id} value={emp._id}>{emp.name || emp.email}</option>
                        ))}
                      </select>
                      {isActive && (
                        <>
                          <button title="Assign Content" className={`business-icon-btn ${assigningClient === client._id ? 'active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); handleOpenAssign(client._id); }}>
                            <FaPlus />
                          </button>
                          <button title="Manage & Progress" className={`business-icon-btn ${managingClient === client._id ? 'active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); handleOpenManage(client._id); }}>
                            <FaCog />
                          </button>
                          <button title="Nutrition" className={`business-icon-btn ${nutritionPlannerMember?.orgMemberId === client._id ? 'active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); setNutritionPlannerMember(nutritionPlannerMember?.orgMemberId === client._id ? null : { orgMemberId: client._id, goalId: null }); }}>
                            <FaUtensils />
                          </button>
                        </>
                      )}
                      {isActive && (
                        <span className="business-expand-icon">
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Assign Panel */}
                  {isActive && assigningClient === client._id && (
                    <div className="business-assign-panel">
                      <h4>Assign Content</h4>
                      <div className="business-assign-type-toggle">
                        <button className={assignType === 'program' ? 'active' : ''} onClick={() => { setAssignType('program'); setAssignContentId(''); }}>Program</button>
                        <button className={assignType === 'workout' ? 'active' : ''} onClick={() => { setAssignType('workout'); setAssignContentId(''); }}>Workout</button>
                      </div>
                      <select className="business-assign-select" value={assignContentId} onChange={(e) => setAssignContentId(e.target.value)}>
                        <option value="">Select {assignType}...</option>
                        {(assignType === 'program' ? availablePrograms : availableWorkouts).map(item => (
                          <option key={item._id} value={item._id}>{item.name}</option>
                        ))}
                      </select>
                      <textarea className="business-assign-notes" placeholder="Notes for client (optional)"
                        value={assignNotes} onChange={(e) => setAssignNotes(e.target.value)} rows={2} />
                      <p className="business-assign-hint">This will appear in your client's "My Workouts" page.</p>
                      <div className="business-assign-actions">
                        <button className="business-btn business-btn-primary" onClick={() => handleAssignContent(client._id)}>
                          Assign to Client
                        </button>
                        <button className="business-btn business-btn-secondary" onClick={() => setAssigningClient(null)}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {/* Manage & Progress Panel */}
                  {isActive && managingClient === client._id && (
                    <div className="business-manage-panel">
                      {/* Assigned Content */}
                      <div className="business-manage-section">
                        <h4><FaClipboardList /> Assigned Content</h4>
                        {(clientSchedules[client._id] || []).length === 0 ? (
                          <p className="business-empty-hint">No content assigned yet.</p>
                        ) : (
                          <div className="business-schedule-list">
                            {(clientSchedules[client._id] || []).map(item => (
                              <div key={item._id} className="business-schedule-item">
                                <span className={`schedule-type-icon ${item.type}`}>
                                  {item.type === 'program' ? <FaDumbbell /> : item.type === 'workout' ? <FaRunning /> : <FaDumbbell />}
                                </span>
                                <div className="schedule-item-info">
                                  <strong>{item.programId?.name || item.workoutId?.name || item.exerciseId?.name || 'Unknown'}</strong>
                                  <span className="schedule-item-meta">
                                    {item.type} · <span className={`schedule-status status-${item.status}`}>{item.status}</span>
                                    {item.notes && <> · {item.notes}</>}
                                  </span>
                                </div>
                                {item.status !== 'completed' && (
                                  <button className="business-icon-btn danger" title="Unassign"
                                    onClick={() => handleUnassign(client._id, item._id)}>
                                    <FaTrash />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Workout Session History */}
                      <div className="business-manage-section">
                        <h4><FaRunning /> Recent Workout Sessions</h4>
                        {(() => {
                          const sessions = clientProgressData[client._id]?.sessions || [];
                          if (sessions.length === 0) return <p className="business-empty-hint">No workout sessions recorded yet.</p>;
                          return (
                            <div className="business-session-list">
                              {sessions.slice(0, 10).map(session => (
                                <div key={session._id} className="business-session-item">
                                  <div className="session-item-main">
                                    <strong>{session.workoutName || 'Workout'}</strong>
                                    <span className="session-item-date">{formatDate(session.startedAt || session.date)}</span>
                                  </div>
                                  <div className="session-item-stats">
                                    <span className={`session-status ${session.status}`}>{session.status}</span>
                                    {session.durationMinutes > 0 && <span>{formatDuration(session.durationMinutes)}</span>}
                                    {session.exercisesCompleted > 0 && (
                                      <span>{session.exercisesCompleted} exercises</span>
                                    )}
                                    {session.rating > 0 && <span>{'★'.repeat(session.rating)}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Nutrition Plans */}
                      <div className="business-manage-section">
                        <h4><FaUtensils /> Nutrition Plans</h4>
                        {(() => {
                          const plans = clientNutritionData[client._id] || [];
                          if (plans.length === 0) return (
                            <div className="business-empty-hint">
                              <p>No nutrition plans yet.</p>
                              <button className="business-btn business-btn-small business-btn-primary"
                                onClick={() => setNutritionPlannerMember({ orgMemberId: client._id, goalId: null })}>
                                Create Nutrition Plan
                              </button>
                            </div>
                          );
                          return (
                            <div className="business-nutrition-list">
                              {plans.map(plan => (
                                <div key={plan._id} className="business-nutrition-item"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => setNutritionPlannerMember({ orgMemberId: client._id, goalId: plan._id })}>
                                  <div className="nutrition-item-main">
                                    <strong>{plan.name}</strong>
                                    {plan.isActive && <span className="nutrition-active-badge">Active</span>}
                                  </div>
                                  <div className="nutrition-item-stats">
                                    <span>{plan.dailyCalories} cal</span>
                                    <span>{plan.dailyProtein}g protein</span>
                                    <span>{plan.dailyCarbs}g carbs</span>
                                    <span>{plan.dailyFat}g fat</span>
                                  </div>
                                  <span className="nutrition-item-date">Updated {formatDate(plan.updatedAt)}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Embedded Nutrition Planner */}
                      {nutritionPlannerMember?.orgMemberId === client._id && (
                        <div className="business-nutrition-embed">
                          <NutritionPlannerWizard
                            embedded
                            propOrgMemberId={nutritionPlannerMember.orgMemberId}
                            propGoalId={nutritionPlannerMember.goalId}
                            onClose={() => {
                              setNutritionPlannerMember(null);
                              loadActivitySummary(client._id);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  // ─── Join Settings Tab ─────────────────────────────────────────
  function renderJoinSettings() {
    return (
      <div className="business-join-settings">
        <div className="business-section-header">
          <h2>Join Settings</h2>
        </div>

        {qrData ? (
          <div className="business-join-content">
            <div className="business-qr-section">
              <h3>QR Code</h3>
              {qrData.qrDataUrl && (
                <img
                  className="business-qr-image"
                  src={qrData.qrDataUrl}
                  alt="Join QR Code"
                />
              )}
            </div>

            <div className="business-join-details">
              {qrData.joinUrl && (
                <div className="business-join-row">
                  <label>Join Link</label>
                  <div className="business-copy-field">
                    <input type="text" readOnly value={qrData.joinUrl} />
                    <button
                      className="business-btn business-btn-small"
                      onClick={() => copyToClipboard(qrData.joinUrl, 'Join link')}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}

              {qrData.joinCode && (
                <div className="business-join-row">
                  <label>Join Code</label>
                  <div className="business-copy-field">
                    <span className="business-join-code">{qrData.joinCode}</span>
                    <button
                      className="business-btn business-btn-small"
                      onClick={() => copyToClipboard(qrData.joinCode, 'Join code')}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              )}

              <div className="business-join-row">
                <label>Require Verification</label>
                <div className="business-toggle-row">
                  <button
                    className={`business-toggle ${requireVerification ? 'active' : ''}`}
                    onClick={handleToggleVerification}
                    role="switch"
                    aria-checked={requireVerification}
                  >
                    <span className="business-toggle-knob" />
                  </button>
                  <span className="business-toggle-label">
                    {requireVerification
                      ? 'New clients must be verified before access'
                      : 'Clients get immediate access on join'}
                  </span>
                </div>
              </div>

              <button
                className="business-btn business-btn-danger"
                onClick={handleRegenerate}
                disabled={regenerating}
              >
                {regenerating ? 'Regenerating...' : 'Regenerate Join Link'}
              </button>
            </div>
          </div>
        ) : (
          <div className="business-loading-inline">
            <div className="loading-spinner" />
            <p>Loading join settings...</p>
          </div>
        )}
      </div>
    );
  }

  // ─── Billing Tab ───────────────────────────────────────────────
  function renderBilling() {
    const usage = stats?.usage || {};
    const lim = stats?.limits || limits || {};

    const usageBars = [
      { label: 'Employees', current: usage.employees ?? employees.length, max: lim.maxEmployees },
      { label: 'Clients', current: usage.clients ?? clients.length, max: lim.maxClients }
    ];

    return (
      <div className="business-billing">
        <div className="business-section-header">
          <h2>Billing & Subscription</h2>
        </div>

        <div className="business-tier-display">
          <span className={`business-tier-badge tier-${tier}`}>{tier || 'free'} plan</span>
        </div>

        <div className="business-usage-section">
          <h3>Usage</h3>
          {usageBars.map((bar) => {
            const pct = bar.max ? Math.min((bar.current / bar.max) * 100, 100) : 0;
            return (
              <div key={bar.label} className="business-usage-bar-row">
                <div className="business-usage-bar-label">
                  <span>{bar.label}</span>
                  <span>{bar.current} / {bar.max ?? '∞'}</span>
                </div>
                <div className="business-usage-bar-track">
                  <div
                    className={`business-usage-bar-fill ${pct >= 90 ? 'critical' : pct >= 70 ? 'warning' : ''}`}
                    style={{ width: `${bar.max ? pct : 0}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="business-billing-actions">
          <button
            className="business-btn business-btn-secondary"
            onClick={() => navigate('/pricing')}
          >
            Manage Subscription
          </button>
          <button
            className="business-btn business-btn-primary"
            onClick={() => navigate('/pricing')}
          >
            Upgrade
          </button>
        </div>
      </div>
    );
  }
}
